import { motion, AnimatePresence } from "framer-motion";
import { t } from "@lingui/macro";
import { Separator, Button, ScrollArea } from "@reactive-resume/ui";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { PlusCircle, CircleNotch, Lock } from "@phosphor-icons/react";
import { useAnalysisContext } from "@/client/contexts/analysis-context";

import { BasicsSection } from "../basics";
import { SummarySection } from "../summary";
import { SectionBase } from "../shared/section-base";
import type {
  Award,
  Certification,
  CustomSection,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  Skill,
  Volunteer,
} from "@reactive-resume/schema";
import { TemplateSection } from "@/client/pages/builder/sidebar/sections/template";
import { LayoutSection } from "@/client/pages/builder/sidebar/sections/layout";
import { TypographySection } from "@/client/pages/builder/sidebar/sections/typography";
import { ThemeSection } from "@/client/pages/builder/sidebar/sections/theme";
import { CssSection } from "@/client/pages/builder/sidebar/sections/css";
import { PageSection } from "@/client/pages/builder/sidebar/sections/page";
import { StatisticsSection } from "@/client/pages/builder/sidebar/sections/statistics";
import { ExportSection } from "@/client/pages/builder/sidebar/sections/export";
import { NotesSection } from "@/client/pages/builder/sidebar/sections/notes";
import { CoverLetterSection } from "@/client/pages/builder/sidebar/sections/cover-letter";
import { useViewStore } from "@/client/stores/view";

// Animation variants for slide-in effect
const slideVariants = {
  initial: { x: -20, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -20, opacity: 0 },
  transition: { duration: 0.2, ease: "easeOut" },
};

type CustomSectionType = {
  id: string;
  name: string;
  columns: number;
  separateLinks: boolean;
  visible: boolean;
  items: Array<{
    summary: string;
    id: string;
    name: string;
    location: string;
    url: { label: string; href: string };
    date: string;
    visible: boolean;
    keywords: string[];
    description: string;
  }>;
};

type ContentProps = {
  customSections: Record<string, CustomSectionType>;
  addSection: () => void;
};

export const SidebarContent = ({ customSections, addSection }: ContentProps) => {
  const separatorClass = "-mx-2 w-[calc(100%+16px)] bg-border/35";
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [openedDataSection, setOpenedDataSection] = useState<string | undefined>(undefined);
  const setActiveSidebar = useViewStore((state) => state.setActiveSidebar);
  const activeSidebar = useViewStore((state) => state.activeSidebar);

  // Get optimization states from context
  const { isSubscribed, isOptimizing, isLoadingOptimization } = useAnalysisContext();

  // Check if optimization is in progress
  const isOptimizationInProgress = isSubscribed || isOptimizing || isLoadingOptimization;

  const scrollIntoView = (selector: string) => {
    // Don't scroll if optimization is in progress
    if (isOptimizationInProgress) return;

    const section = containerRef.current?.querySelector(selector);
    section?.scrollIntoView({ behavior: "smooth" });
  };

  const clickSection = useCallback(
    async (event: MessageEvent<any>) => {
      // Don't handle section clicks if optimization is in progress
      if (isOptimizationInProgress) return;

      if (event.data.type === "section-clicked") {
        if (activeSidebar !== "data") {
          setActiveSidebar("data");
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        setOpenedDataSection(event.data.id);
        setTimeout(() => {
          scrollIntoView(`#${event.data.id}`);
        }, 100);
      }
    },
    [activeSidebar, isOptimizationInProgress],
  );

  useEffect(() => {
    window.removeEventListener("message", clickSection);
    window.addEventListener("message", clickSection);
    return () => {
      window.removeEventListener("message", clickSection);
    };
  }, [clickSection]);

  // Optimization overlay component
  const OptimizationOverlay = () => (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/40 dark:bg-black/20 backdrop-blur-xs">
      <div className="bg-card flex flex-col items-center justify-center space-y-4 rounded-lg p-8">
        <div className="flex items-center space-x-3">
          <Lock weight="bold" className="text-muted-foreground size-8" />
        </div>
        <div className="text-center">
          <h3 className="text-foreground text-lg font-semibold">{t`During optimization, you can't edit your data.`}</h3>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex">
      <AnimatePresence mode="wait">
        {activeSidebar === "data" && (
          <motion.div
            key="data"
            initial="initial"
            animate="animate"
            exit="exit"
            className="relative flex w-full"
            variants={slideVariants}
          >
            <ScrollArea
              orientation="vertical"
              className={`h-screen flex-1 pb-16 lg:pb-0 ${
                isOptimizationInProgress ? "pointer-events-none overflow-hidden" : ""
              }`}
              style={isOptimizationInProgress ? { overflow: "hidden" } : undefined}
            >
              <div
                ref={containerRef}
                key="data"
                className={`@container/left grid flex-1 p-3 [&>section]:py-4 ${
                  isOptimizationInProgress ? "pointer-events-none overflow-hidden" : ""
                }`}
                style={
                  isOptimizationInProgress ? { overflow: "hidden", height: "100%" } : undefined
                }
              >
                <BasicsSection />
                <Separator className={separatorClass} />
                <SummarySection
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                />
                <Separator className={separatorClass} />
                <SectionBase<Profile>
                  id="profiles"
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  title={(item) => item.network}
                  description={(item) => item.username}
                />
                <Separator className={separatorClass} />
                <SectionBase<Experience>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="experience"
                  title={(item) => item.company}
                  description={(item) => item.position}
                />
                <Separator className={separatorClass} />
                <SectionBase<Education>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="education"
                  title={(item) => item.institution}
                  description={(item) => item.area}
                />
                <Separator className={separatorClass} />
                <SectionBase<Skill>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="skills"
                  title={(item) => item.name}
                  description={(item) => {
                    if (item.description) return item.description;
                    if (item.keywords.length > 0) return `${item.keywords.length} keywords`;
                  }}
                />
                <Separator className={separatorClass} />
                <SectionBase<Language>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="languages"
                  title={(item) => item.name}
                  description={(item) => item.description}
                />
                <Separator className={separatorClass} />
                <SectionBase<Award>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="awards"
                  title={(item) => item.title}
                  description={(item) => item.awarder}
                />
                <Separator className={separatorClass} />
                <SectionBase<Certification>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="certifications"
                  title={(item) => item.name}
                  description={(item) => item.issuer}
                />
                <Separator className={separatorClass} />
                <SectionBase<Interest>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="interests"
                  title={(item) => item.name}
                  description={(item) => {
                    if (item.keywords.length > 0) return `${item.keywords.length} keywords`;
                  }}
                />
                <Separator className={separatorClass} />
                <SectionBase<Project>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="projects"
                  title={(item) => item.name}
                  description={(item) => item.description}
                />
                <Separator className={separatorClass} />
                <SectionBase<Publication>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="publications"
                  title={(item) => item.name}
                  description={(item) => item.publisher}
                />
                <Separator className={separatorClass} />
                <SectionBase<Volunteer>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="volunteer"
                  title={(item) => item.organization}
                  description={(item) => item.position}
                />
                <Separator className={separatorClass} />
                <SectionBase<Reference>
                  setOpenedDataSection={setOpenedDataSection}
                  openedDataSection={openedDataSection}
                  id="references"
                  title={(item) => item.name}
                  description={(item) => item.description}
                />

                {/* Custom Sections */}
                {Object.values(customSections).map((section) => (
                  <Fragment key={section.id}>
                    <Separator className={separatorClass} />
                    <SectionBase<CustomSection>
                      setOpenedDataSection={setOpenedDataSection}
                      openedDataSection={openedDataSection}
                      id={`custom.${section.id}`}
                      title={(item) => item.name}
                      description={(item) => item.description}
                    />
                  </Fragment>
                ))}

                <Separator className={separatorClass} />
                <Button
                  size="lg"
                  variant="outline"
                  onClick={addSection}
                  disabled={isOptimizationInProgress}
                >
                  <PlusCircle />
                  <span className="ml-2">{t`Add a new section`}</span>
                </Button>
              </div>
            </ScrollArea>

            {/* Show overlay when optimization is in progress */}
            {isOptimizationInProgress && <OptimizationOverlay />}
          </motion.div>
        )}

        {activeSidebar === "design" && (
          <motion.div
            key="design"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={slideVariants}
            className="@container/right grid flex-1 gap-y-4 p-4"
          >
            <TemplateSection />
            <Separator />
            <LayoutSection />
            <Separator />
            <TypographySection />
            <Separator />
            <ThemeSection />
            <Separator />
            <CssSection />
            <Separator />
            <PageSection />
          </motion.div>
        )}

        {activeSidebar === "share" && (
          <motion.div
            key="note"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={slideVariants}
            className="@container/right grid flex-1 gap-y-4 p-4"
          >
            <StatisticsSection />
            <ExportSection />
            <Separator />
            {/*<NotesSection />*/}
            <CoverLetterSection />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
